// src/frontend/src/components/Common/CreditsMeter.tsx - Enhanced with both Solo Core and Solo Pro options for trial users
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';

interface CreditsMeterProps {
  creditsUsed: number;
  creditsTotal: number;
  planType: 'solo-core' | 'solo-pro' | 'team-pilot' | 'free-trial';
  showUpgradePrompt?: boolean;
  trialDaysLeft?: number;
}

const CreditsMeter: React.FC<CreditsMeterProps> = ({ 
  creditsUsed, 
  creditsTotal, 
  planType,
  showUpgradePrompt = true,
  trialDaysLeft
}) => {
  const navigate = useNavigate();
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  
  const percentage = (creditsUsed / creditsTotal) * 100;
  const remaining = creditsTotal - creditsUsed;
  
  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const getColorClass = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPlanName = () => {
    switch (planType) {
      case 'solo-core': return 'Solo Core';
      case 'solo-pro': return 'Solo Pro';
      case 'team-pilot': return 'Team Pilot';
      case 'free-trial': return 'Free Trial';
      default: return 'Plan';
    }
  };

  const getUpgradeMessage = () => {
    if (planType === 'free-trial') {
      if (trialDaysLeft && trialDaysLeft <= 3) {
        return `Trial expires in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}! Choose your plan to continue.`;
      }
      if (percentage >= 75) {
        return 'Running low on trial sessions! Choose a plan to continue practicing.';
      }
      return 'Choose your plan to continue coaching after your trial.';
    }
    
    if (planType === 'solo-core') {
      if (percentage >= 90) {
        return 'Almost out of sessions! Upgrade to Pro for 3x more.';
      }
      if (percentage >= 75) {
        return 'Running low! Pro gives you 3x more sessions + real-time coaching.';
      }
      return 'Upgrade to Pro for 3x more sessions + real-time coaching tips!';
    }
    
    return 'Need more sessions?';
  };

  const shouldShowUpgrade = () => {
    // Always show upgrade options for trial and solo-core users
    return planType === 'free-trial' || planType === 'solo-core';
  };

  const isUrgent = () => {
    return (planType === 'free-trial' && trialDaysLeft && trialDaysLeft <= 3) || percentage >= 90;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">Coaching Sessions</h3>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">{getPlanName()}</span>
          {planType === 'free-trial' && trialDaysLeft && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              trialDaysLeft <= 3 ? 'bg-red-100 text-red-800' : 
              trialDaysLeft <= 7 ? 'bg-yellow-100 text-yellow-800' : 
              'bg-blue-100 text-blue-800'
            }`}>
              {trialDaysLeft} days left
            </span>
          )}
        </div>
      </div>
      
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{creditsUsed} used</span>
          <span className="font-medium text-gray-900">{remaining} remaining</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`${getColorClass()} h-2 rounded-full transition-all duration-300 ${
              isUrgent() ? 'animate-pulse' : ''
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{Math.round(percentage)}% used</span>
          <span>Resets monthly</span>
        </div>
      </div>

      {/* ENHANCED: Show both Solo Core and Solo Pro options for trial users */}
      {shouldShowUpgrade() && (
        <div className={`border-t pt-3 ${isUrgent() ? 'bg-red-50 -m-4 mt-3 p-4 rounded-b-lg' : ''}`}>
          <p className={`text-xs mb-3 ${isUrgent() ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
            {getUpgradeMessage()}
          </p>
          
          {/* Both subscription options for trial users */}
          {planType === 'free-trial' && (
            <div className="space-y-3">
              {/* Solo Core - Continue with what they're trying */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-gray-900">Continue with Solo Core</h5>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">What you're trying</span>
                </div>
                <div className="text-lg font-bold text-gray-900 mb-1">
                  $19<span className="text-sm font-normal text-gray-600">/month</span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  20 sessions/month • PDF reports • All scenarios
                </p>
                <button
                  onClick={() => createCheckoutSession('solo_core', 'monthly')}
                  disabled={isCreatingCheckout}
                  className="w-full text-center px-3 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {isCreatingCheckout ? 'Setting up...' : 'Subscribe to Solo Core'}
                </button>
              </div>

              {/* Solo Pro - The upgrade option */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 relative">
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Most Popular
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2 mt-1">
                  <h5 className="text-sm font-medium text-blue-900">Upgrade to Solo Pro</h5>
                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">3x more!</span>
                </div>
                <div className="text-lg font-bold text-blue-600 mb-1">
                  $39<span className="text-sm font-normal text-gray-600">/month</span>
                </div>
                <p className="text-xs text-blue-700 mb-3">
                  <strong>60 sessions/month • Real-time tips • Custom scenarios</strong>
                </p>
                <button
                  onClick={() => createCheckoutSession('solo_pro', 'monthly')}
                  disabled={isCreatingCheckout}
                  className={`w-full text-center px-3 py-2 text-sm font-medium rounded transition-colors disabled:opacity-50 ${
                    isUrgent() 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isCreatingCheckout ? 'Setting up...' : 'Upgrade to Solo Pro'}
                </button>
              </div>
            </div>
          )}
          
          {/* Solo Core users - just show Pro upgrade */}
          {planType === 'solo-core' && (
            <div className="space-y-2">
              <button
                onClick={() => createCheckoutSession('solo_pro', 'monthly')}
                disabled={isCreatingCheckout}
                className={`block w-full text-center px-3 py-2 text-sm font-medium rounded transition-colors disabled:opacity-50 ${
                  percentage >= 90 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isCreatingCheckout ? 'Setting up...' : 'Upgrade to Pro - $39/month'}
              </button>
            </div>
          )}
          
          {/* Alternative: Link to pricing page */}
          <Link
            to="/pricing"
            className="block w-full text-center px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 border-t pt-2 mt-3"
          >
            Compare all plans in detail →
          </Link>
        </div>
      )}

      {/* Pro plan comparison for Solo Core users */}
      {planType === 'solo-core' && percentage >= 50 && (
        <div className="mt-3 pt-3 border-t border-gray-200 bg-blue-25">
          <div className="text-xs text-gray-600 mb-2">
            <strong>Pro gives you:</strong>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-gray-500">Current: 20/month</div>
            <div className="text-blue-600 font-medium">Pro: 60/month</div>
            <div className="text-gray-500">Basic feedback</div>
            <div className="text-blue-600 font-medium">Real-time tips</div>
          </div>
        </div>
      )}

      {/* Trial urgency indicator */}
      {planType === 'free-trial' && trialDaysLeft && trialDaysLeft <= 1 && (
        <div className="mt-3 pt-3 border-t border-red-200 bg-red-50 -m-4 mt-3 p-4 rounded-b-lg">
          <div className="flex items-center text-red-700">
            <svg className="w-4 h-4 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-xs font-medium">
              Trial expires {trialDaysLeft === 1 ? 'tomorrow' : 'today'}!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsMeter;